import { InlineNotification } from '@carbon/react';
import bbox from '@turf/bbox';
import { resourcesAtom } from 'atoms/root';
import { DevelopmentAreaMap } from 'components/development-area-map';
import { EmptyState } from 'components/empty-state';
import { PolygonArea } from 'components/map';
import NoMapSvg from 'images/srp-illustration-no-map.svg';
import { useAtomValue } from 'jotai';
import request from 'lib/request';
import { isEqual, uniq } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReferenceLocationResponse } from 'types/api-responses';
import { mapConfigAtom } from '../atoms';
import { TRACT_GEOMETRY_STYLE } from '../tract-map';
import type { TractGeometryResponse } from '../types';
import { bulkMapRefAtom, mapFocusAtom, showMapAtom } from './atoms';
import styles from './bulk-upload.module.scss';
import { GIS_SHAPE_NOTE, MapRow, mapBlocker } from './map';

type Bounds = [[number, number], [number, number]];

/** How View asset on map frames a row: close, but the section stays in view. */
export const FLY_TO_ROW = { padding: 40, maxZoom: 14, duration: 800 };

interface Geography {
  id: number;
  key: string;
  referenceLocation: ReferenceLocationResponse;
  quarterCalls: string[];
  status?: 'trash';
  highlighted?: boolean;
}

const geographyKey = (row: MapRow) => {
  const calls = row.gisShape ? [] : row.quarterCalls;
  return `${row.referenceLocationId}|${calls.join(',')}|${row.invalid}`;
};

/**
 * One geography per distinct survey area, quarter calls and validity (rows
 * that share them share a polygon), plus the focused row on top. Location and
 * quarter-call objects are kept across renders while their values hold, since
 * each polygon refetches its shape when they change.
 * @param {MapRow[]} rows - Rows with a resolved survey area
 * @param {string | null} focusedKey - The focused row's key
 * @return {Geography[]} The map's geographies
 */
const useGeographies = (rows: MapRow[], focusedKey: string | null) => {
  const cache = useRef(new Map<string, Geography>());
  return useMemo(() => {
    const next = new Map<string, Geography>();
    const build = (row: MapRow, key: string) => {
      const previous = cache.current.get(key) ?? next.get(key);
      const calls = row.gisShape ? [] : row.quarterCalls;
      const status = row.invalid ? ('trash' as const) : undefined;
      const geography =
        previous &&
        previous.id === row.referenceLocationId &&
        previous.referenceLocation.name === row.surveyArea &&
        previous.status === status &&
        isEqual(previous.quarterCalls, calls)
          ? previous
          : {
              id: row.referenceLocationId as number,
              key,
              referenceLocation: {
                id: row.referenceLocationId,
                name: row.surveyArea,
              } as ReferenceLocationResponse,
              quarterCalls: calls,
              status,
            };
      next.set(key, geography);
      return geography;
    };
    const shared = new Map<string, Geography>();
    rows.forEach((row) => {
      const key = geographyKey(row);
      if (!shared.has(key)) shared.set(key, build(row, key));
    });
    const focused = rows.find((row) => row.key === focusedKey);
    const list = [...shared.values()];
    if (focused) {
      list.push({
        ...build(focused, `focus-${focused.key}`),
        highlighted: true,
      });
    }
    cache.current = next;
    return list;
  }, [rows, focusedKey]);
};

/**
 * The created tract's geometry, for a row whose asset exists (GET
 * /crm/tracts/:id, the wizard's TractMap source).
 * @param {string | null} assetId - The Salesforce tract id
 * @return {GeoJSON.Geometry | null | undefined} The geometry; null without
 *   an asset or geometry (or on a failed request); undefined while loading
 */
const useTractGeometry = (assetId: string | null) => {
  const [loaded, setLoaded] = useState<{
    assetId: string;
    geometry: GeoJSON.Geometry | null;
  } | null>(null);
  useEffect(() => {
    if (!assetId) return undefined;
    const abortController = new AbortController();
    request
      .get<TractGeometryResponse>(
        `/crm/tracts/${encodeURIComponent(assetId)}`,
        abortController
      )
      .then((response) => {
        if (abortController.signal.aborted) return;
        setLoaded({
          assetId,
          geometry: 'error' in response ? null : (response.geojson ?? null),
        });
      });
    return () => abortController.abort();
  }, [assetId]);
  if (!assetId) return null;
  return loaded?.assetId === assetId ? loaded.geometry : undefined;
};

const geometryBounds = (geometry: GeoJSON.Geometry): Bounds => {
  const [minLng, minLat, maxLng, maxLat] = bbox(geometry);
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
};

const isUsableBounds = ([[x1, y1], [x2, y2]]: Bounds) =>
  [x1, y1, x2, y2].every(Number.isFinite) && (x1 !== x2 || y1 !== y2);

interface RowsMapProps {
  rows: MapRow[];
  focused: MapRow | null;
}

/**
 * Every mapped row's survey area (invalid rows in red), the focused row
 * highlighted, and its created tract's geometry when it has one. Each View
 * asset on map flies to the focused row: its tract, else its survey area.
 * @param {RowsMapProps} props - Mapped rows and the focused one
 * @return {JSX.Element} The map
 */
const RowsMap = ({ rows, focused }: RowsMapProps) => {
  const focus = useAtomValue(mapFocusAtom);
  const mapRef = useAtomValue(bulkMapRefAtom);
  const resources = useAtomValue(resourcesAtom);
  const geographies = useGeographies(rows, focused?.key ?? null);
  const geometry = useTractGeometry(focused?.assetId ?? null);
  const focusedLocationId = focused?.referenceLocationId ?? null;
  // A map opened by View asset on map starts framed on that row. The ids stay
  // put while it is open: a new set re-frames the map with a jump, which
  // would cut short the fly to a row.
  const [openedOn] = useState(focusedLocationId);
  const referenceLocationIds = useMemo(() => {
    if (openedOn !== null) return [openedOn];
    return uniq(rows.map((row) => row.referenceLocationId as number));
  }, [openedOn, rows]);
  const boundingBoxHref = resources?.boundingBoxes.href;

  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map || !focus || focusedLocationId === null) return undefined;
    // Wait for the tract, so a created row flies once, to the right shape.
    if (geometry === undefined) return undefined;
    const abortController = new AbortController();
    const fly = (bounds: Bounds) => {
      if (abortController.signal.aborted || !isUsableBounds(bounds)) return;
      map.fitBounds(bounds, FLY_TO_ROW);
    };
    if (geometry) {
      fly(geometryBounds(geometry));
    } else if (boundingBoxHref) {
      request
        .post<{ points: Bounds }, { referenceLocationIds: number[] }>(
          boundingBoxHref,
          { referenceLocationIds: [focusedLocationId] },
          abortController
        )
        .then((response) => {
          if (response.type === 'success') fly(response.data.points);
        });
    }
    return () => abortController.abort();
  }, [mapRef, focus, focusedLocationId, geometry, boundingBoxHref]);

  return (
    <DevelopmentAreaMap
      referenceLocationIds={referenceLocationIds}
      geographies={geographies}
      mapConfigAtom={mapConfigAtom}
      refAtom={bulkMapRefAtom}
    >
      {geometry && (
        <PolygonArea
          id="bulk-tract-geometry"
          geometry={geometry}
          style={TRACT_GEOMETRY_STYLE}
        />
      )}
    </DevelopmentAreaMap>
  );
};

const GisShapeNote = () => (
  <InlineNotification
    kind="warning"
    lowContrast
    hideCloseButton
    title=""
    subtitle={GIS_SHAPE_NOTE}
  />
);

const NoSurveyAreas = () => (
  <EmptyState
    icon={NoMapSvg}
    headerText="No Survey Areas Selected"
    helperText="Please select a survey area to get started."
  />
);

/**
 * The inline map above the table while Show Map is on: every row with a
 * resolved survey area, valid or not, focused on the last row chosen with
 * View asset on map.
 * @param {object} props - The rows on the page
 * @param {MapRow[]} props.rows - Every row, mapped or not
 * @return {JSX.Element | null} The map, or null while hidden
 */
export const BulkMap = ({ rows }: { rows: MapRow[] }) => {
  const showMap = useAtomValue(showMapAtom);
  const focus = useAtomValue(mapFocusAtom);
  const mapped = useMemo(() => rows.filter((row) => !mapBlocker(row)), [rows]);
  if (!showMap) return null;
  const focused = mapped.find((row) => row.key === focus?.key) ?? null;
  return (
    <div className={styles.mapSlot} data-testid="bulk-map-slot">
      {focused?.gisShape && <GisShapeNote />}
      <div className={styles.mapCanvas}>
        {mapped.length === 0 ? (
          <NoSurveyAreas />
        ) : (
          <RowsMap rows={mapped} focused={focused} />
        )}
      </div>
    </div>
  );
};

/**
 * The map beside the Add / Edit asset form: the row being edited, following
 * its survey area and quarter calls as they change.
 * @param {object} props - The row as the form has it
 * @param {MapRow} props.row - The draft row
 * @return {JSX.Element} The map, or the empty state until a survey area resolves
 */
export const AssetMap = ({ row }: { row: MapRow }) => {
  const rows = useMemo(() => [row], [row]);
  const geographies = useGeographies(rows, null);
  if (mapBlocker(row)) return <NoSurveyAreas />;
  return (
    <div className={styles.assetMap}>
      {row.gisShape && <GisShapeNote />}
      <div className={styles.mapCanvas}>
        <DevelopmentAreaMap
          referenceLocationIds={[row.referenceLocationId as number]}
          geographies={geographies}
          mapConfigAtom={mapConfigAtom}
        />
      </div>
    </div>
  );
};
